import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import type {
  ApplyDutyTemplatePayload,
  CreateDutyTemplatePayload,
  DutyTemplate,
  DutyTemplateItem,
  UpdateDutyTemplatePayload,
} from '../models/duty-template.model';
import { HomeDataService } from './home-data.service';

@Injectable({ providedIn: 'root' })
export class DutyTemplateService {
  #http = inject(HttpClient);
  #queryClient = inject(QueryClient);
  #homeData = inject(HomeDataService);

  readonly templatesQuery = injectQuery(() => ({
    queryKey: ['dutyTemplates'],
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http.get<DutyTemplate[]>(
          `${environment.baseUrl}/duties/templates`,
        ),
      ),
  }));

  createTemplate = injectMutation(() => ({
    mutationFn: (payload: CreateDutyTemplatePayload) =>
      lastValueFrom(
        this.#http.post<DutyTemplate>(
          `${environment.baseUrl}/duties/templates`,
          payload,
        ),
      ),
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['dutyTemplates'] });
    },
  }));

  updateTemplate = injectMutation(() => ({
    mutationFn: (payload: UpdateDutyTemplatePayload) =>
      lastValueFrom(
        this.#http.patch<DutyTemplate>(
          `${environment.baseUrl}/duties/templates`,
          payload,
        ),
      ),
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['dutyTemplates'] });
    },
  }));

  deleteTemplate = injectMutation(() => ({
    mutationFn: (templateId: string) =>
      lastValueFrom(
        this.#http.delete<void>(`${environment.baseUrl}/duties/templates`, {
          body: { templateId },
        }),
      ),
    onSuccess: () => {
      void this.#queryClient.invalidateQueries({ queryKey: ['dutyTemplates'] });
    },
  }));

  applyTemplate = injectMutation(() => ({
    mutationFn: (payload: ApplyDutyTemplatePayload) =>
      lastValueFrom(
        this.#http.post<{ id: string }[]>(
          `${environment.baseUrl}/duties/templates/apply`,
          payload,
        ),
      ),
    onSuccess: () => {
      this.#homeData.invalidateParentDuties();
    },
  }));

  async applyResolvedRoutine(
    name: string,
    items: DutyTemplateItem[],
    kidIds: string[],
  ): Promise<void> {
    const template = await this.createTemplate.mutateAsync({ name, items });
    await this.applyTemplate.mutateAsync({
      templateId: template.id,
      kidIds,
    });
  }
}
